/** Editorial clock: Israel, 24h, Latin digits. Avoids SSR/client Intl drift. */
const HEBREW_DATE_OPTS = {
  timeZone: 'Asia/Jerusalem',
  numberingSystem: 'latn',
} as const;

/**
 * Format a date as a long Hebrew date string, e.g. "21 באפריל 2026, 14:30".
 * Uses native Intl so we get proper RTL numerals + month names without
 * shipping a localization library.
 */
export function formatHebrewDate(date: Date | string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = new Intl.DateTimeFormat('he-IL', {
    ...HEBREW_DATE_OPTS,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
  const timePart = new Intl.DateTimeFormat('he-IL', {
    ...HEBREW_DATE_OPTS,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(d);
  return `${datePart}, ${timePart}`;
}

/**
 * Estimate Hebrew reading time. The 250 wpm baseline is the typical
 * silent-reading rate for prose; articles below 30 seconds round up to
 * "דקה" so we never display "0 דקות".
 */
export function getReadingTime(body: string): { minutes: number; label: string } {
  const text   = (body || '').replace(/!\[.*?\]\(.*?\)/g, ' ').replace(/[#>*_`~\-]/g, ' ');
  const words  = text.trim().split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.round(words / 250));
  const label   = minutes === 1 ? 'דקת קריאה' : `${minutes} דקות קריאה`;
  return { minutes, label };
}

export function getTimeAgo(date: Date | string): string {
  const now = new Date();
  const articleDate = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - articleDate.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'לפני רגע';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `לפני ${diffInMinutes} דקות`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `לפני ${diffInHours} שעות`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `לפני ${diffInDays} ימים`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `לפני ${diffInWeeks} שבועות`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `לפני ${diffInMonths} חודשים`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `לפני ${diffInYears} שנים`;
}
